import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isSameDay,
  startOfDay,
  endOfDay,
  subWeeks,
  subMonths,
} from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { utils, writeFile } from "xlsx";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import StatusChip from "components/StatusChip";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE, API_HOST } from "config/api";

// Cell Components with PropTypes
const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const MemberCell = ({ value, row, onClick }) => (
  <Box
    display="flex"
    alignItems="center"
    lineHeight={1}
    onClick={() => onClick(row.original)}
    sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
  >
    <Avatar sx={{ width: 36, height: 36, fontSize: 14 }} />
    <Box ml={2} lineHeight={1}>
      <Typography display="block" variant="button" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  </Box>
);
MemberCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const AmountCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
AmountCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const MethodCell = ({ value }) => (
  <Box ml={-1}>
    <StatusChip value={value} color={value === "cash" ? "success" : "info"} />
  </Box>
);
MethodCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const DateCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
DateCell.propTypes = {
  value: PropTypes.string.isRequired,
};

// Create a wrapper component for MemberCell with proper PropTypes
const MemberCellWrapper = ({ value, row, onClick }) => (
  <MemberCell value={value} row={row} onClick={onClick} />
);
MemberCellWrapper.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

// Member Details Modal Component with PropTypes
const MemberDetailsModal = ({ member, open, onClose, churchId }) => {
  const [welfareData, setWelfareData] = useState({
    month: [],
    year: [],
    all: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open && member) {
      fetchMemberWelfare();
    }
  }, [open, member]);

  const fetchMemberWelfare = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      // Fetch welfare for different time periods
      const periods = [
        {
          key: "month",
          start: format(subMonths(new Date(), 1), "yyyy-MM-dd"),
          end: format(new Date(), "yyyy-MM-dd"),
        },
        {
          key: "year",
          start: format(subMonths(new Date(), 12), "yyyy-MM-dd"),
          end: format(new Date(), "yyyy-MM-dd"),
        },
        {
          key: "all",
          start: null,
          end: null,
        },
      ];

      const welfareData = { month: [], year: [], all: [] };

      for (const period of periods) {
        let url = `${API_BASE}/report/member/${member.member_id || member.id}/welfare`;

        if (period.start && period.end) {
          url += `?start_date=${period.start}&end_date=${period.end}`;
        }

        const response = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          welfareData[period.key] = data || [];
          console.log(`Fetched ${data} records for ${period.key}`);
        }
      }

      setWelfareData(welfareData);
    } catch (error) {
      console.error("Failed to fetch member welfare:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load member welfare details",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₵${parseFloat(amount).toLocaleString("en-GH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const renderWelfareTable = (data) => {
    if (loading) {
      return <Typography>Loading welfare data...</Typography>;
    }

    if (!data || data.length === 0) {
      return <Typography>No welfare records found.</Typography>;
    }

    const totalAmount = data.reduce((sum, record) => sum + (parseFloat(record.amount) || 0), 0);

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {data.length} Payment Record{data.length !== 1 ? "s" : ""} - Total:{" "}
          {formatCurrency(totalAmount)}
        </Typography>
        <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
          {data.map((record, index) => (
            <Box key={index} mb={2} p={1} sx={{ border: "1px solid #e0e0e0", borderRadius: "4px" }}>
              <Typography variant="caption" display="block">
                <strong>Type:</strong> {record.type || "N/A"}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Amount:</strong> {formatCurrency(record.amount)}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Method:</strong> {record.method || "N/A"}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Date:</strong>{" "}
                {record.created_at ? format(new Date(record.created_at), "MMM d, yyyy") : "N/A"}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Reference:</strong> {record.reference || "N/A"}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  if (!member) return null;

  return (
    <Box
      display={open ? "block" : "none"}
      position="fixed"
      top={0}
      left={0}
      width="100%"
      height="100%"
      bgcolor="rgba(0,0,0,0.5)"
      zIndex={9999}
      onClick={onClose}
    >
      <Box
        position="absolute"
        top="50%"
        left="50%"
        width="90%"
        maxWidth="600px"
        maxHeight="80vh"
        bgcolor="background.paper"
        borderRadius="8px"
        boxShadow={24}
        p={4}
        sx={{
          transform: "translate(-50%, -50%)",
          overflow: "auto",
          backgroundColor: (theme) => theme.palette.background.paper,
          boxShadow: (theme) => theme.shadows[5],
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Member Payment Details</Typography>
          <CloseIcon onClick={onClose} sx={{ cursor: "pointer" }} />
        </Box>

        <Box display="flex" alignItems="center" mb={3}>
          <Avatar sx={{ width: 58, height: 58, fontSize: 14 }} />
          <Box ml={2}>
            <Typography variant="h6">{member.memberName}</Typography>
          </Box>
        </Box>

        <Box borderBottom={1} borderColor="divider" mb={2}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box display="flex">
              <Typography
                variant="button"
                sx={{
                  mr: 2,
                  cursor: "pointer",
                  borderBottom: activeTab === 0 ? "2px solid" : "none",
                  pb: 1,
                  color: activeTab === 0 ? "primary.main" : "text.secondary",
                }}
                onClick={() => setActiveTab(0)}
              >
                Past Month
              </Typography>
              <Typography
                variant="button"
                sx={{
                  mr: 2,
                  cursor: "pointer",
                  borderBottom: activeTab === 1 ? "2px solid" : "none",
                  pb: 1,
                  color: activeTab === 1 ? "primary.main" : "text.secondary",
                }}
                onClick={() => setActiveTab(1)}
              >
                Past Year
              </Typography>
              <Typography
                variant="button"
                sx={{
                  cursor: "pointer",
                  borderBottom: activeTab === 2 ? "2px solid" : "none",
                  pb: 1,
                  color: activeTab === 2 ? "primary.main" : "text.secondary",
                }}
                onClick={() => setActiveTab(2)}
              >
                All Time
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box mt={2}>
          {activeTab === 0 && renderWelfareTable(welfareData.month)}
          {activeTab === 1 && renderWelfareTable(welfareData.year)}
          {activeTab === 2 && renderWelfareTable(welfareData.all)}
        </Box>
      </Box>
    </Box>
  );
};

MemberDetailsModal.propTypes = {
  member: PropTypes.shape({
    member_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    memberName: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  churchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function useWelfareData() {
  const { church } = useAuth();
  const [memberData, setMemberData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Type", accessor: "type", align: "left", width: "15%" },
      { Header: "Amount (₵)", accessor: "amount", align: "left", width: "15%", Cell: AmountCell },
      { Header: "Method", accessor: "method", align: "center", width: "10%", Cell: MethodCell },
      { Header: "Reference", accessor: "reference", align: "center", width: "10%" },
      {
        Header: "Payment Date",
        accessor: "paymentDate",
        align: "center",
        width: "10%",
        Cell: DateCell,
      },
    ],
    rows: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [reportType, setReportType] = useState("welfare");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberDetails, setShowMemberDetails] = useState(false);

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setShowMemberDetails(true);
  };

  const closeMemberDetails = () => {
    setShowMemberDetails(false);
    setSelectedMember(null);
  };

  // Create MemberCell component with click handler
  const MemberCellWithHandler = ({ value, row }) => (
    <MemberCellWrapper value={value} row={row} onClick={handleMemberClick} />
  );
  MemberCellWithHandler.propTypes = {
    value: PropTypes.string.isRequired,
    row: PropTypes.shape({
      original: PropTypes.object.isRequired,
    }).isRequired,
  };

  // Update columns to include Member column
  const columnsWithMember = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    {
      Header: "Member",
      accessor: "memberName",
      align: "left",
      width: "25%",
      Cell: MemberCellWithHandler,
    },
    ...memberData.columns.slice(1), // Include all other columns
  ];

  const getReportTitle = () => {
    const typeMap = {
      welfare: "Welfare",
      tithe: "Tithe",
      offering: "Offering",
      donation: "Donation",
    };

    const typeName = typeMap[reportType] || "Payment";

    if (!dateRange.startDate) return `${typeName} Report`;

    if (dateRange.endDate && !isSameDay(dateRange.startDate, dateRange.endDate)) {
      return `${typeName} Report (${format(dateRange.startDate, "MMM d, yyyy")} - ${format(
        dateRange.endDate,
        "MMM d, yyyy"
      )})`;
    }

    return `${typeName} Report - ${format(dateRange.startDate, "MMM d, yyyy")}`;
  };

  const calculateTotalAmount = (payments) => {
    return payments.reduce((sum, payment) => {
      return sum + (parseFloat(payment.amount) || 0);
    }, 0);
  };

  const formatCurrency = (amount) => {
    return `₵${parseFloat(amount).toLocaleString("en-GH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const fetchWelfareData = async (startDate, endDate, type = "welfare") => {
    try {
      setIsLoading(true);
      setError(null);
      setReportType(type);

      if (!church?.id) {
        throw new Error("Church context not available");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const params = new URLSearchParams();
      params.append("type", type);

      if (startDate && endDate) {
        params.append("start_date", format(startDate, "yyyy-MM-dd"));
        params.append("end_date", format(endDate, "yyyy-MM-dd"));
      }

      const response = await fetch(
        `${API_BASE}/accounts/report/${church.id}/${type}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch payment data");
      }

      const result = await response.json();
      const payments = result.data || [];

      // Calculate total amount
      const calculatedTotal = calculateTotalAmount(payments);
      setTotalAmount(calculatedTotal);

      const rows = payments.map((payment, index) => ({
        id: payment.id,
        serial: index + 1,
        memberName: payment.member
          ? `${payment.member.first_name || ""} ${payment.member.surname || ""}`.trim()
          : "N/A",
        member_id: payment.member?.id,
        type: payment.type || "N/A",
        amount: payment.amount ? formatCurrency(payment.amount) : formatCurrency(0),
        method: payment.method || "N/A",
        reference: payment.reference || "N/A",
        paymentDate: payment.created_at
          ? format(new Date(payment.created_at), "MMM d, yyyy")
          : "N/A",
        originalData: payment,
      }));

      setMemberData((prev) => ({ ...prev, rows }));
      setTotalMembers(payments.length);
      setDateRange({
        startDate: startDate || new Date(),
        endDate: endDate || new Date(),
      });
    } catch (error) {
      console.error("Failed to fetch payment data:", error);
      setError(error.message);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to load payment data",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);

    const today = new Date();
    let startDate, endDate;

    switch (newPeriod) {
      case "daily":
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case "weekly":
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case "monthly":
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case "yearly":
        startDate = startOfYear(today);
        endDate = endOfYear(today);
        break;
      default:
        startDate = null;
        endDate = null;
    }

    if (startDate && endDate) {
      fetchWelfareData(startDate, endDate, reportType);
    } else {
      fetchWelfareData(null, null, reportType);
    }
  };

  const handleCustomDateChange = (start, end) => {
    setPeriod("custom");
    fetchWelfareData(start, end, reportType);
  };

  const handleTypeChange = (type) => {
    setReportType(type);
    fetchWelfareData(dateRange.startDate, dateRange.endDate, type);
  };

  const exportToCSV = () => {
    const headers = columnsWithMember.map((col) => col.Header);
    const data = memberData.rows.map((row) => {
      return columnsWithMember.map((col) => row[col.accessor] || "");
    });

    // Add total amount row
    data.push(["", "", "Total Amount", formatCurrency(totalAmount), "", "", ""]);

    const csvContent = [headers.join(","), ...data.map((row) => row.join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${reportType}_report_${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const title = getReportTitle();

    // Title
    doc.setFontSize(16);
    doc.text(title, 14, 15);

    // Church name
    if (church?.name) {
      doc.setFontSize(12);
      doc.text(church.name, 14, 22);
    }

    // Report details
    doc.setFontSize(10);
    doc.text(
      `Period: ${dateRange.startDate ? format(dateRange.startDate, "MMM d, yyyy") : "All time"} ${
        dateRange.endDate && !isSameDay(dateRange.startDate, dateRange.endDate)
          ? ` - ${format(dateRange.endDate, "MMM d, yyyy")}`
          : ""
      }`,
      14,
      30
    );
    doc.text(`Total Records: ${totalMembers}`, 14, 35);
    doc.text(`Total Amount: ${formatCurrency(totalAmount)}`, 14, 40);

    const headers = columnsWithMember.map((col) => col.Header);
    const data = memberData.rows.map((row) => {
      return columnsWithMember.map((col) => row[col.accessor] || "");
    });

    // Add total amount row
    data.push(["", "", "Total", formatCurrency(totalAmount), "", "", ""]);

    doc.autoTable({
      head: [headers],
      body: data,
      startY: 45,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: "left",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "center" }, // SN column
        3: { halign: "right" }, // Amount column
        4: { halign: "center" }, // Method column
        5: { halign: "center" }, // Reference column
        6: { halign: "center" }, // Date column
      },
      footStyles: {
        fillColor: [221, 221, 221],
        textColor: 0,
        fontStyle: "bold",
      },
    });

    // Footer
    doc.setFontSize(8);
    doc.text(
      `Generated on ${format(new Date(), "MMM d, yyyy h:mm a")}`,
      14,
      doc.internal.pageSize.height - 10
    );

    doc.save(`${reportType}_report_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  const printReport = () => {
    const printWindow = window.open("", "_blank");
    const title = getReportTitle();

    const htmlContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .church-name {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .report-title {
              font-size: 16px;
              margin-bottom: 10px;
            }
            .report-details {
              margin-bottom: 15px;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              font-size: 12px;
            }
            th {
              background-color: #2980b9;
              color: white;
              text-align: left;
              padding: 8px;
              border: 1px solid #ddd;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
            }
            .amount-cell {
              text-align: right;
            }
            .center-cell {
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f2f2f2;
            }
            .total-row {
              font-weight: bold;
              background-color: #e6f2ff !important;
            }
            .footer {
              margin-top: 20px;
              font-size: 11px;
              text-align: right;
              color: #666;
            }
            @media print {
              body {
                margin: 0;
                padding: 10px;
              }
              .no-print {
                display: none;
              }
              table {
                width: 100% !important;
              }
              th, td {
                padding: 6px !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${church?.name ? `<div class="church-name">${church.name}</div>` : ""}
            <div class="report-title">${title}</div>
            <div class="report-details">
              <div><strong>Period:</strong> ${
                dateRange.startDate ? format(dateRange.startDate, "MMM d, yyyy") : "All time"
              } ${
      dateRange.endDate && !isSameDay(dateRange.startDate, dateRange.endDate)
        ? ` - ${format(dateRange.endDate, "MMM d, yyyy")}`
        : ""
    }</div>
              <div><strong>Total Records:</strong> ${totalMembers}</div>
              <div><strong>Total Amount:</strong> ${formatCurrency(totalAmount)}</div>
            </div>
          </div>

          <button class="no-print" onclick="window.print()" style="margin-bottom: 10px; padding: 5px 10px; background: #2980b9; color: white; border: none; border-radius: 3px; cursor: pointer;">Print Report</button>
          <button class="no-print" onclick="window.close()" style="margin-bottom: 10px; padding: 5px 10px; background: #ccc; color: #333; border: none; border-radius: 3px; cursor: pointer;">Close</button>

          <table>
            <thead>
              <tr>
                ${columnsWithMember.map((col) => `<th>${col.Header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${memberData.rows
                .map(
                  (row) => `
                <tr>
                  <td class="center-cell">${row.serial}</td>
                  <td>${row.memberName}</td>
                  <td>${row.type}</td>
                  <td class="amount-cell">${row.amount}</td>
                  <td class="center-cell">${row.method}</td>
                  <td class="center-cell">${row.reference}</td>
                  <td class="center-cell">${row.paymentDate}</td>
                </tr>
              `
                )
                .join("")}
              <tr class="total-row">
                <td colspan="3" class="center-cell">Total</td>
                <td class="amount-cell">${formatCurrency(totalAmount)}</td>
                <td colspan="3"></td>
              </tr>
            </tbody>
          </table>

          <div class="footer no-print">
            Generated on ${format(new Date(), "MMM d, yyyy h:mm a")}
          </div>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExport = (type) => {
    switch (type) {
      case "csv":
        exportToCSV();
        break;
      case "pdf":
        exportToPDF();
        break;
      case "print":
        printReport();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (church?.id) {
      fetchWelfareData();
    }
  }, [church?.id]);

  return {
    columns: columnsWithMember,
    rows: memberData.rows,
    isLoading,
    error,
    period,
    dateRange,
    reportType,
    totalAmount,
    totalMembers,
    handlePeriodChange,
    handleCustomDateChange,
    handleTypeChange,
    handleExport,
    fetchWelfareData,
    getReportTitle,
    formatCurrency,
    selectedMember,
    showMemberDetails,
    handleMemberClick,
    closeMemberDetails,
    churchId: church?.id,
    MemberDetailsModal,
  };
}
